import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

import api from "../../services/api";
import {
  listAutomationFlows,
  createAutomationFlow,
  updateAutomationFlow,
  deleteAutomationFlow,
} from "../../services/automationFlowService";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  dialogField: {
    marginBottom: theme.spacing(2),
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    marginTop: -theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(0.5),
  },
}));

const TRIGGER_LABELS = {
  lead_created: "Novo Lead",
  first_response: "Lead Respondeu",
};

const TRIGGER_DESCRIPTIONS = {
  lead_created:
    "Dispara quando um novo lead/ticket é criado. Ideal para enviar boas-vindas e mover o card para a coluna correta.",
  first_response:
    "Dispara quando o lead envia a primeira mensagem após o fluxo de boas-vindas. Ideal para mover o card para 'Tem interesse'.",
};

const EMPTY_FORM = {
  name: "",
  triggerType: "lead_created",
  flowId: "",
  isActive: true,
};

const AutomationFlows = () => {
  const classes = useStyles();

  const [automations, setAutomations] = useState([]);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadAutomations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAutomationFlows();
      setAutomations(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAutomations();
  }, [loadAutomations]);

  // Carregar os fluxos do FlowBuilder para o seletor
  useEffect(() => {
    api
      .get("/flowbuilder")
      .then(({ data }) => setFlows(data.flows || []))
      .catch(() => {});
  }, []);

  const handleOpenCreate = () => {
    setEditingAutomation(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = (automation) => {
    setEditingAutomation(automation);
    setForm({
      name: automation.name,
      triggerType: automation.triggerType,
      flowId: automation.flowId || "",
      isActive: automation.isActive,
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingAutomation(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    const payload = {
      name: form.name.trim(),
      triggerType: form.triggerType,
      flowId: form.flowId || null,
      isActive: form.isActive,
    };

    try {
      if (editingAutomation) {
        const updated = await updateAutomationFlow(editingAutomation.id, payload);
        setAutomations((prev) =>
          prev.map((a) => (a.id === updated.id ? updated : a))
        );
        toast.success("Automação atualizada");
      } else {
        const created = await createAutomationFlow(payload);
        setAutomations((prev) => [...prev, created]);
        toast.success("Automação criada");
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleActive = async (automation) => {
    try {
      const updated = await updateAutomationFlow(automation.id, {
        isActive: !automation.isActive,
      });
      setAutomations((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a))
      );
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAutomationFlow(deletingId);
      setAutomations((prev) => prev.filter((a) => a.id !== deletingId));
      toast.success("Automação excluída");
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmOpen(false);
      setDeletingId(null);
    }
  };

  const getFlowName = (automation) => {
    if (automation.flow) return automation.flow.name;
    if (automation.flowId) {
      const f = flows.find((f) => f.id === automation.flowId);
      return f ? f.name : `Fluxo #${automation.flowId}`;
    }
    return "—";
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Automações</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Nova Automação
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Gatilho</TableCell>
              <TableCell>Fluxo de Conversas</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              automations.map((automation) => (
                <TableRow key={automation.id}>
                  <TableCell>{automation.name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        TRIGGER_LABELS[automation.triggerType] ||
                        automation.triggerType
                      }
                      color={
                        automation.triggerType === "lead_created"
                          ? "primary"
                          : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>{getFlowName(automation)}</TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={automation.isActive}
                      onChange={() => handleToggleActive(automation)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(automation)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingId(automation.id);
                          setConfirmOpen(true);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {!loading && automations.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhuma automação cadastrada
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog criar/editar */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingAutomation ? "Editar Automação" : "Nova Automação"}
        </DialogTitle>
        <DialogContent>
          <TextField
            className={classes.dialogField}
            label="Nome"
            name="name"
            value={form.name}
            onChange={handleChange}
            fullWidth
            required
            variant="outlined"
            size="small"
          />

          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            className={classes.dialogField}
          >
            <InputLabel>Gatilho</InputLabel>
            <Select
              name="triggerType"
              value={form.triggerType}
              onChange={handleChange}
              label="Gatilho"
            >
              <MenuItem value="lead_created">Novo Lead criado</MenuItem>
              <MenuItem value="first_response">
                Primeira resposta do lead
              </MenuItem>
            </Select>
          </FormControl>

          {form.triggerType && (
            <Typography className={classes.helperText}>
              {TRIGGER_DESCRIPTIONS[form.triggerType]}
            </Typography>
          )}

          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            className={classes.dialogField}
          >
            <InputLabel>Fluxo de Conversas</InputLabel>
            <Select
              name="flowId"
              value={form.flowId}
              onChange={handleChange}
              label="Fluxo de Conversas"
            >
              <MenuItem value="">— Nenhum fluxo selecionado —</MenuItem>
              {flows.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                  {!f.active && (
                    <Typography
                      component="span"
                      variant="caption"
                      style={{ marginLeft: 8, color: "#999" }}
                    >
                      (inativo)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography className={classes.helperText}>
            Crie e configure o fluxo em{" "}
            <strong>Fluxo de Conversas</strong> usando os blocos disponíveis
            (mensagem, mover coluna, tags, etc.) e depois selecione-o aqui.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir Automação"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        Tem certeza que deseja excluir esta automação?
      </ConfirmationModal>
    </MainContainer>
  );
};

export default AutomationFlows;
